/**
 * Reusable kop surat (letterhead) document used by both PrintEstimate (auth)
 * and SharePage (public). Pass `doc` (calculation) + `cfg` (hospital_config).
 */
import { formatRupiahDot, formatDate } from "../lib/format";
import { Check } from "@phosphor-icons/react";
import CustomTableView from "./ai/CustomTableView";

export default function LetterheadDoc({ doc, cfg }) {
  const today = new Date(doc.created_at);
  const docNum = `${String(today.getDate()).padStart(3, "0")}/${
    cfg.doc_no_prefix || "ADMS"
  }/${today.getMonth() + 1}/${today.getFullYear()}`;
  const custom = doc.doc_type === "CUSTOM" ? doc.custom_table : null;
  const opType = doc.operation_type || (custom ? "" : "ELEKTIF");
  const cityName = "Tangerang";
  const title = custom ? custom.judul : "TARIF BIAYA TINDAKAN OPERASI";
  const keterangan = custom ? custom.keterangan || [] : null;

  return (
    <div
      className="max-w-[900px] mx-auto bg-white print-paper my-6 shadow-md print:shadow-none print:my-0"
      data-testid="print-document"
    >
      <div className="px-10 py-8">
        {/* Kop Surat */}
        <div className="flex items-end justify-between gap-4 pb-3 border-b-2 border-slate-900">
          <div className="flex items-end gap-3">
            {cfg.logo_url ? (
              <img
                src={cfg.logo_url}
                alt="logo"
                className="object-contain"
                style={{
                  maxWidth: `${cfg.logo_max_width || 600}px`,
                  maxHeight: `${cfg.logo_max_height || 100}px`,
                }}
                data-testid="print-logo"
              />
            ) : (
              <div className="text-xl font-black text-slate-900">{cfg.name}</div>
            )}
          </div>
          <div className="text-right text-blue-700 underline text-sm">
            {cfg.website}
          </div>
        </div>
        <div className="text-[11px] text-slate-700 pt-1.5 pb-3 border-b-2 border-slate-900">
          {cfg.address}
          {cfg.phone ? ` - Tlp ${cfg.phone}` : ""}
          {cfg.fax ? ` - Fax. ${cfg.fax}` : ""}
        </div>

        <h1
          className="text-2xl font-black tracking-tight text-center mt-6 mb-6 underline"
          data-testid="print-title"
        >
          {title}
        </h1>

        <div className="grid grid-cols-[160px_1fr] gap-y-2 text-[13px] mb-6">
          <div className="font-semibold">Nama pasien</div>
          <div className="border-b border-slate-400" data-testid="print-nama">
            : {doc.nama_pasien}
          </div>

          <div className="font-semibold">Umur</div>
          <div className="border-b border-slate-400" data-testid="print-umur">
            : {doc.umur || "-"}
          </div>

          <div className="font-semibold">No Mr / Registrasi</div>
          <div className="border-b border-slate-400" data-testid="print-nomr">
            : {doc.no_mr || "-"}
          </div>

          <div className="font-semibold">Ruang / Kelas</div>
          <div className="border-b border-slate-400">
            : {doc.ruang_kelas} {doc.kelas ? `(${doc.kelas})` : ""}
          </div>

          <div className="font-semibold">Nama Dr DPJP</div>
          <div className="border-b border-slate-400" data-testid="print-dpjp">
            : {doc.dpjp || doc.dokter_operator || "-"}
          </div>

          <div className="font-semibold">{custom ? "Jenis Layanan" : "Nama Operasi"}</div>
          {custom && !opType ? (
            <div className="border-b border-slate-400" data-testid="print-jenis-layanan">
              : {custom.judul}
            </div>
          ) : (
          <div className="flex items-center gap-4 flex-wrap">
            <span>:</span>
            {[
                { v: "CITO", l: "Cito" },
                { v: "ELEKTIF", l: "Elektif" },
                { v: "PERMINTAAN_WAKTU", l: "Permintaan Waktu" },
              ].map((x) => (
                <span key={x.v} className="flex items-center gap-1.5">
                  <span
                    className={`w-4 h-4 inline-flex items-center justify-center border border-slate-600 ${
                      opType === x.v ? "bg-slate-900 text-white" : "bg-white"
                    }`}
                  >
                    {opType === x.v && <Check size={12} weight="bold" />}
                  </span>
                  <span>{x.l}</span>
                </span>
              ))}
          </div>
          )}

          <div className="font-semibold">Alasan</div>
          <div className="border-b border-slate-400" data-testid="print-alasan">
            :{" "}
            {doc.alasan ||
              (doc.result?.tindakan_breakdown || [])
                .map((t) => t.nama_tindakan || t.kategori)
                .join(", ")}
          </div>
        </div>

        {custom ? (
          <div className="border-2 border-slate-900 p-5 mb-6" data-testid="print-custom-block">
            <h2 className="text-center font-black text-lg mb-3">RINCIAN BIAYA</h2>
            <CustomTableView table={custom} testIdPrefix="print-custom" />
          </div>
        ) : (
          <div className="border-2 border-slate-900 p-5 mb-6">
            <h2 className="text-center font-black text-lg mb-3">
              PERKIRAAN BIAYA OPERASI
            </h2>
            <ol className="space-y-1.5 text-[13px] tabular-nums">
              {(doc.result?.aggregated_components || []).map((a, i) => (
                <li
                  key={i}
                  className="grid grid-cols-[26px_1fr_220px]"
                  data-testid={`print-line-${i}`}
                >
                  <span className="font-bold">{i + 1}.</span>
                  <span>{a.label}</span>
                  <span>: {formatRupiahDot(a.total)}</span>
                </li>
              ))}
              <li
                className="grid grid-cols-[26px_1fr_220px] pt-2 mt-1 border-t border-slate-400"
                data-testid="print-total"
              >
                <span></span>
                <span className="font-black text-base">TOTAL</span>
                <span className="font-black text-base">
                  : {formatRupiahDot(doc.grand_total)}
                </span>
              </li>
            </ol>
          </div>
        )}

        <div className="text-[12px] mb-8">
          <div className="italic font-bold underline mb-1">KETERANGAN</div>
          {keterangan ? (
            keterangan.length > 0 ? (
              <ol className="list-decimal pl-6 space-y-0.5 mt-1" data-testid="print-custom-keterangan">
                {keterangan.map((k, i) => (
                  <li key={i}>{k}</li>
                ))}
              </ol>
            ) : (
              <div className="text-slate-500">-</div>
            )
          ) : (
            <>
              <div className="font-medium">Perkiraan biaya diatas belum termasuk:</div>
              <ol className="list-decimal pl-6 space-y-0.5 mt-1">
                <li>Biaya penyulit &amp; Komplikasi</li>
                <li>
                  Biaya penunjang medik (Lab, PA, Radiologi Diagnostik, elektromedik)
                </li>
                <li>
                  Biaya kamar perawatan, visite dokter, jasa keperawatan, alkes, dan
                  obat-obatan, DLL
                </li>
                <li>
                  Untuk Operasi CITO ditambah 30% dari jasa dokter operator dan jasa
                  dokter anestesi
                </li>
                <li>
                  Biaya tambahan termasuk biaya administrasi rawat inap sebesar 5% dari
                  total biaya perawatan.
                </li>
                <li>
                  Untuk kondisi operasi elektif yang dijadwalkan dihari libur (minggu)
                  atau tanggal merah atas permintaan pasien maka jasa dokter operator,
                  dokter asisten dan dr anestesi + 30%, jasa sewa kamar operasi, sewa
                  kamar RR, sewa instrument dan jasa paramedis + 15% dari harga yang
                  telah di tetapkan
                </li>
              </ol>
            </>
          )}
        </div>

        <div className="flex justify-between text-[13px] mt-12">
          <div>
            <div>
              {cityName} :{" "}
              <span className="border-b border-slate-700 inline-block min-w-[160px]">
                {formatDate(doc.created_at).split(",")[0]}
              </span>
            </div>
            <div className="mt-1">Menyetujui &amp; Memahami</div>
            <div>Pasien / Keluarga</div>
            <div className="h-16" />
            <div>( Ttd &amp; Nama Jelas )</div>
          </div>
          <div className="text-right">
            <div className="h-[22px]" />
            <div>&nbsp;</div>
            <div className="font-medium">Petugas Admission</div>
            <div className="h-16" />
            <div>( Ttd &amp; Nama Jelas )</div>
            {doc.created_by && (
              <div
                className="mt-1 text-[12px] font-semibold text-slate-800"
                data-testid="print-created-by"
              >
                {doc.created_by}
              </div>
            )}
          </div>
        </div>

        <div className="mt-12 pt-2 border-t border-slate-300 flex items-center justify-between text-[11px] text-slate-600">
          <div>Page 1/1</div>
          <div>{docNum}</div>
        </div>
      </div>
    </div>
  );
}
